(function (window, document) {
    'use strict';

    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

    const DEFAULT_DELIVERY_VARIANT_ID = '52824633213256';

    function getData() {
        return window.FreeBouquets || {};
    }

    function getOrders() {
        const data = getData();
        return Array.isArray(data.orders) ? data.orders : [];
    }

    function hasCustomer() {
        return Boolean(getData().customer);
    }

    function escapeHtml(str) {
        return String(str || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function formatMoney(pence) {
        const value = Number(pence || 0) / 100;
        return '£' + value.toFixed(2);
    }

    function getOrderTime(order) {
        return new Date(order.created_at).getTime();
    }

    function isPaidFinancialStatus(order) {
        const status = String(order.financial_status_label || '').trim().toUpperCase();

        return status === 'PAID' || status === 'PARTIALLY_REFUNDED';
    }

    function isFulfilledOrder(order) {
        return String(order.status || '').trim().toUpperCase() === 'FULFILLED';
    }

    function isBouquetLineItem(item) {
        return item && item.product_type === 'Bouquet';
    }

    function getLineItemPrice(item) {
        return Number(item && item.original_price ? item.original_price : 0);
    }

    function hasBouquetLine(order) {
        return Array.isArray(order.line_items) &&
            order.line_items.some(function (item) {
                return isBouquetLineItem(item);
            });
    }

    function hasPaidBouquetLine(order) {
        return Array.isArray(order.line_items) &&
            order.line_items.some(function (item) {
                return isBouquetLineItem(item) && getLineItemPrice(item) > 0;
            });
    }

    function isZeroBouquetOrder(order) {
        return Number(order.total_price || 0) === 0 && hasBouquetLine(order);
    }

    function sortOrdersAsc(orders) {
        return (orders || [])
            .slice()
            .sort(function (a, b) {
                return getOrderTime(a) - getOrderTime(b);
            });
    }

    function getCycleOrders(orders) {
        const ordersAsc = sortOrdersAsc(orders);

        let lastResetIndex = -1;

        for (let i = ordersAsc.length - 1; i >= 0; i--) {
            if (isZeroBouquetOrder(ordersAsc[i])) {
                lastResetIndex = i;
                break;
            }
        }

        return lastResetIndex >= 0
            ? ordersAsc.slice(lastResetIndex + 1)
            : ordersAsc;
    }

    function getPaidBouquetOrders(orders) {
        return getCycleOrders(orders)
            .filter(function (order) {
                return isFulfilledOrder(order) &&
                    isPaidFinancialStatus(order) &&
                    hasPaidBouquetLine(order);
            })
            .sort(function (a, b) {
                return getOrderTime(a) - getOrderTime(b);
            });
    }

    function getBouquetStatsFromOrders(orders) {
        let bouquetCount = 0;
        let bouquetTotal = 0;

        (orders || []).forEach(function (order) {
            if (!Array.isArray(order.line_items)) return;

            order.line_items.forEach(function (item) {
                if (!isBouquetLineItem(item)) return;

                const price = getLineItemPrice(item);

                if (price > 0) {
                    bouquetCount += 1;
                    bouquetTotal += price;
                }
            });
        });

        return {
            bouquetCount: bouquetCount,
            bouquetTotal: bouquetTotal,
            bouquetAverage: bouquetCount > 0 ? bouquetTotal / bouquetCount : 0
        };
    }

    function getDaysLeftFromOrder(order) {
        if (!order) return 0;

        const elapsedDays = Math.floor(
            (Date.now() - getOrderTime(order)) / (1000 * 60 * 60 * 24)
        );

        return Math.max(30 - elapsedDays, 0);
    }

    function formatDate(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);

        return day + '.' + month + '.' + year;
    }

    function getRewardState(inputOrders) {
        const orders = inputOrders || getOrders();

        const paidBouquetOrders = getPaidBouquetOrders(orders);

        const latestPaidBouquetOrder = paidBouquetOrders[paidBouquetOrders.length - 1] || null;
        const previousPaidBouquetOrder = paidBouquetOrders[paidBouquetOrders.length - 2] || null;

        const latestOrderStillActive = Boolean(
            latestPaidBouquetOrder &&
            Date.now() - getOrderTime(latestPaidBouquetOrder) <= THIRTY_DAYS_MS
        );

        const lastTwoOrdersWithin30Days = Boolean(
            latestPaidBouquetOrder &&
            previousPaidBouquetOrder &&
            getOrderTime(latestPaidBouquetOrder) - getOrderTime(previousPaidBouquetOrder) <= THIRTY_DAYS_MS
        );

        const isUnlocked = Boolean(
            latestOrderStillActive &&
            lastTwoOrdersWithin30Days
        );

        const isAlmost = Boolean(
            !isUnlocked &&
            latestPaidBouquetOrder &&
            latestOrderStillActive
        );

        const stats = getBouquetStatsFromOrders(paidBouquetOrders);

        let state = 'locked';
        let title = 'Locked reward';
        let subtitle = 'Complete 2 orders to unlock free bouquets';

        if (isUnlocked) {
            state = 'unlocked';
            title = 'Get your free bouquet';
            subtitle = '';
        } else if (isAlmost) {
            state = 'almost';
            title = 'Almost there';
            subtitle = '1 more order to unlock free bouquets';
        }

        let promoEndDate = null;
        let promoEndDateFormatted = '';

        if (latestPaidBouquetOrder) {
            promoEndDate = new Date(latestPaidBouquetOrder.created_at);
            promoEndDate.setDate(promoEndDate.getDate() + 30);
            promoEndDateFormatted = formatDate(promoEndDate);
        }

        return {
            state: state,

            isLocked: state === 'locked',
            isAlmost: state === 'almost',
            isUnlocked: state === 'unlocked',

            title: title,
            subtitle: subtitle,

            paidBouquetOrders: paidBouquetOrders,
            latestPaidBouquetOrder: latestPaidBouquetOrder,
            previousPaidBouquetOrder: previousPaidBouquetOrder,

            latestOrderStillActive: latestOrderStillActive,
            lastTwoOrdersWithin30Days: lastTwoOrdersWithin30Days,

            bouquetCount: stats.bouquetCount,
            bouquetTotal: stats.bouquetTotal,
            bouquetAverage: stats.bouquetAverage,

            daysLeft: getDaysLeftFromOrder(latestPaidBouquetOrder),
            promoEndDate: promoEndDate,
            promoEndDateFormatted: promoEndDateFormatted
        };
    }

    async function loadCollectionData(options) {
        options = options || {};

        const collectionHandle = options.collectionHandle || 'free-bouquets';
        const view = options.view || 'free-bouquets-json';
        const allPages = Boolean(options.allPages);
        const requestedPage = Number(options.page || 1);

        const products = [];
        let collectionTitle = '';
        let page = allPages ? 1 : requestedPage;
        let totalPages = 1;

        while (page <= totalPages) {
            const url = '/collections/' + collectionHandle + '?view=' + view + '&page=' + page;

            const res = await fetch(url, {
                credentials: 'same-origin'
            });

            if (!res.ok) {
                throw new Error('FreeBouquets: failed to load collection');
            }

            const data = await res.json();

            if (!data || !Array.isArray(data.products)) {
                throw new Error('FreeBouquets: invalid collection JSON');
            }

            if (!collectionTitle && data.collection_title) {
                collectionTitle = data.collection_title;
            }

            products.push.apply(products, data.products);

            totalPages = allPages ? Number(data.pages || 1) : page;
            page += 1;
        }

        return {
            products: products,
            title: collectionTitle,
            pages: totalPages
        };
    }

    function productMatchesPrice(product, bouquetAverage) {
        const price = Number(product.price_min || 0);

        return price > 0 && price <= Number(bouquetAverage || 0);
    }

    function filterProductsByReward(products, reward) {
        return (products || []).filter(function (product) {
            return productMatchesPrice(product, reward.bouquetAverage);
        });
    }

    function getProductVariantId(product) {
        if (product.selected_variant && product.selected_variant.id) {
            return product.selected_variant.id;
        }

        if (product.selected_variant_id) {
            return product.selected_variant_id;
        }

        return product.id;
    }

    async function addFreeBouquetToCart(options) {
        options = options || {};

        const variantId = options.variantId;
        const quantity = Number(options.quantity || 1);
        const deliveryVariantId = options.deliveryVariantId || DEFAULT_DELIVERY_VARIANT_ID;

        if (!variantId) {
            throw new Error('FreeBouquets: missing variant id');
        }

        // Через CartService (общая очередь): сброс корзины, если в ней что-то
        // есть, и один add.js с букетом и доставкой. Раньше — clear.js + add.js
        // сырыми fetch мимо очереди.
        const result = await window.CartService.prepareCheckout({
            items: [
                { variantId: variantId, quantity: quantity },
                { variantId: deliveryVariantId, quantity: 1 }
            ],
            clear: 'auto'
        });

        return result.data;
    }

    function bindFreeBouquetForms(selector, options) {
        options = options || {};

        document.querySelectorAll(selector).forEach(function (form) {
            if (form.dataset.freeBouquetBound === 'true') return;

            form.dataset.freeBouquetBound = 'true';

            form.addEventListener('submit', async function (event) {
                event.preventDefault();
                event.stopImmediatePropagation();

                const formData = new FormData(form);

                try {
                    await addFreeBouquetToCart({
                        variantId: formData.get('id'),
                        quantity: formData.get('quantity') || 1,
                        deliveryVariantId: options.deliveryVariantId
                    });
                } catch (err) {
                    console.error(err);
                }

                window.location.href = options.redirectUrl || '/checkout';
            }, true);
        });
    }

    function onReady(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback);
        } else {
            callback();
        }
    }

    window.FreeBouquetsCore = {
        THIRTY_DAYS_MS: THIRTY_DAYS_MS,

        getData: getData,
        getOrders: getOrders,
        hasCustomer: hasCustomer,

        escapeHtml: escapeHtml,
        formatMoney: formatMoney,
        formatDate: formatDate,

        getOrderTime: getOrderTime,
        isZeroBouquetOrder: isZeroBouquetOrder,
        getCycleOrders: getCycleOrders,
        getPaidBouquetOrders: getPaidBouquetOrders,
        getRewardState: getRewardState,
        getDaysLeftFromOrder: getDaysLeftFromOrder,

        loadCollectionData: loadCollectionData,
        productMatchesPrice: productMatchesPrice,
        filterProductsByReward: filterProductsByReward,
        getProductVariantId: getProductVariantId,

        addFreeBouquetToCart: addFreeBouquetToCart,
        bindFreeBouquetForms: bindFreeBouquetForms,

        onReady: onReady
    };
    window.dispatchEvent(new CustomEvent('FreeBouquetsCore:ready'));
})(window, document);